import React from 'react';
import FiltersCard from '../../../components/FiltersCard';
import { Form, FilterGrid, FilterField, type SearchFieldProps } from '@/components/ui';

export interface ActivityFilterState {
  classification: string;
  profile: string;
}

export const EMPTY_ACTIVITY_FILTERS: ActivityFilterState = {
  classification: '',
  profile: '',
};

/**
 * How many filters hide activities (the collapsed bar's "n filters applied"). Activities have
 * no inactive state, so no default hides any (V2-075: no change).
 */
export const countActivityFilters = (f: ActivityFilterState): number =>
  [f.classification !== '', f.profile !== ''].filter(Boolean).length;

/** True when the pending filters differ from the applied ones (enables Apply). */
export const activityFiltersDiffer = (a: ActivityFilterState, b: ActivityFilterState): boolean =>
  a.classification !== b.classification || a.profile !== b.profile;

interface ActivityFiltersProps {
  /** The PENDING filters - editing them fetches nothing until Apply. */
  filters: ActivityFilterState;
  onFilterChange: (name: keyof ActivityFilterState, value: string) => void;
  onApply: () => void;
  /** Pass `!hasUnappliedChanges`. */
  applyDisabled: boolean;
  onClearFilters: () => void;
  /** True when the APPLIED filters (or the search) differ from the defaults. */
  hasActiveFilters: boolean;
  activeFilterCount: number;
  profiles: string[];
  /** The page's search, in the panel's header bar (V2-077); Enter runs it within the filters. */
  search: SearchFieldProps;
}

const ActivityFilters: React.FC<ActivityFiltersProps> = ({
  filters,
  onFilterChange,
  onApply,
  applyDisabled,
  onClearFilters,
  hasActiveFilters,
  activeFilterCount,
  profiles,
  search,
}) => (
  <FiltersCard
    title="Activity Filters"
    subtitle="Filter activities by classification and profile"
    collapsible
    defaultCollapsed={false}
    onApply={onApply}
    applyDisabled={applyDisabled}
    onClear={onClearFilters}
    hasActiveFilters={hasActiveFilters}
    appliedFilterCount={activeFilterCount}
    search={search}
  >
    <FilterGrid>
      <FilterField>
        <Form.Label htmlFor="activity-filter-classification">Classification</Form.Label>
        <Form.Select
          id="activity-filter-classification"
          name="classification"
          value={filters.classification}
          onChange={(e) => onFilterChange('classification', e.target.value)}
        >
          <option value="">All Classifications</option>
          <option value="routine">Routine</option>
          <option value="quantified">Quantified</option>
          <option value="overhead">Overhead</option>
        </Form.Select>
      </FilterField>
      <FilterField>
        <Form.Label htmlFor="activity-filter-profile">Profile</Form.Label>
        <Form.Select
          id="activity-filter-profile"
          name="profile"
          value={filters.profile}
          onChange={(e) => onFilterChange('profile', e.target.value)}
        >
          <option value="">All Profiles</option>
          {profiles.map((profile) => (
            <option key={profile} value={profile}>
              {profile}
            </option>
          ))}
        </Form.Select>
      </FilterField>
    </FilterGrid>
  </FiltersCard>
);

export default ActivityFilters;
